/**
 * @template T,R
 * @param {Array<T>} arr
 * @param {(x: T, i: number) => Promise<R>} promiseFn
 * @param {number} batchSize
 * @returns {Promise<Array<R>>}
 */
export function batchPromises(arr, promiseFn, batchSize = 1) {
  if (arr.length === 0) {
    return Promise.resolve([])
  }

  const todo = arr.slice()
  let pending = 0
  let count = 0
  let rejected = false
  const results = []

  return new Promise((resolve, reject) => {
    const next = () => {
      while (!rejected && todo.length && pending < batchSize) {
        const resultIndex = count++
        pending++
        promiseFn(todo.shift(), resultIndex)
          .then(rs => {
            pending--
            results[resultIndex] = rs

            if (!rejected && todo.length === 0 && pending === 0) {
              resolve(results)
            } else {
              next()
            }
          })
          .catch(err => {
            rejected = true
            reject(err)
          })
      }
    }

    next()
  })
}

// Copied from https://stackoverflow.com/a/63286791/890
export function debounceAsync(asyncFunction, delayMs) {
  let timeout
  let [promise, resolve, reject] = makeFuture()
  return function (...args) {
    clearTimeout(timeout)
    timeout = setTimeout(async () => {
      const [prevResolve, prevReject] = [resolve, reject]
      ;[promise, resolve, reject] = makeFuture()
      try {
        prevResolve(await asyncFunction.apply(this, args))
      } catch (error) {
        prevReject(error)
      }
    }, delayMs)
    return promise
  }

  function makeFuture() {
    let savedResolve
    let savedReject
    const promise = new Promise((resolve, reject) => {
      savedResolve = resolve
      savedReject = reject
    })
    return [promise, savedResolve, savedReject]
  }
}

export function delay(fn, timeout) {
  return new Promise(function (resolve, reject) {
    setTimeout(function () {
      fn().then(resolve).catch(reject)
    }, timeout)
  })
}
